import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { requestService } from '../services/api';

const RecoveryForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    debtorName: '',
    amount: '',
    currency: 'XOF',
    recoveryType: 'amiable',
    description: '',
    serviceType: 'recouvrement'
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validation simple
    if (!formData.amount || isNaN(formData.amount)) {
      setError('Veuillez entrer un montant valide');
      setLoading(false);
      return;
    }
    
    try {
      await requestService.submitRequest(formData);
      setSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        companyName: '',
        debtorName: '',
        amount: '',
        currency: 'XOF',
        recoveryType: 'amiable',
        description: '',
        serviceType: 'recouvrement'
      });
      
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        {/* Formulaire */}
        <Grid size={{ xs: 12,  md: 7}}>
          <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
            <Typography variant="h4" gutterBottom color="primary">
              Recouvrement de Créances
            </Typography>
            
            <Typography variant="body1" paragraph color="text.secondary">
              Soumettez votre demande de recouvrement. Notre équipe d'experts
              analysera votre dossier et vous proposera la meilleure stratégie.
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                ✅ Votre demande a été enregistrée. Nous vous contacterons sous 24h.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                ❌ {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12,sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label="Nom complet"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6}}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6}}>
                  <TextField
                    required
                    fullWidth
                    label="Téléphone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6}}>
                  <TextField
                    fullWidth
                    label="Nom de l'entreprise (si applicable)"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6}}>
                  <TextField
                    required
                    fullWidth
                    label="Nom du débiteur"
                    name="debtorName"
                    value={formData.debtorName}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6}}>
                  <TextField
                    required
                    fullWidth
                    label="Montant de la créance"
                    name="amount"
                    type="number"
                    value={formData.amount}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <Typography variant="body2" color="text.secondary">
                          {formData.currency}
                        </Typography>
                      )
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Type de recouvrement souhaité</FormLabel>
                    <RadioGroup
                      row
                      name="recoveryType"
                      value={formData.recoveryType}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="amiable"
                        control={<Radio />}
                        label="Recouvrement amiable"
                      />
                      <FormControlLabel
                        value="judiciaire"
                        control={<Radio />}
                        label="Recouvrement judiciaire"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={4}
                    label="Description de la créance"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Décrivez la nature de la créance, la date d'échéance, les éventuelles preuves..."
                    variant="outlined"
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{ py: 1.5 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Soumettre la demande de recouvrement'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Informations sur les procédures */}
        <Grid size={{ xs: 12, md: 5}}>
          <Box sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" gutterBottom color="primary">
                Procédures de Recouvrement
              </Typography>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Recouvrement Amiable</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Première étape avant toute action judiciaire. Nous contactons le débiteur
                    par lettre recommandée pour trouver une solution à l'amiable.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Injonction de Payer</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Procédure rapide (environ 2 mois), peu coûteuse, sans obligation
                    d'avocat. Idéale pour les créances certaines et liquides.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Référé Provision</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Procédure encore plus rapide (15 jours à 1 mois), pour obtenir
                    une provision sur une créance non contestable.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Assignation au Fond</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Procédure complète devant le tribunal, pour les dossiers complexes
                    ou les montants importants. Nécessite généralement un avocat.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper elevation={2} sx={{ p: 3, backgroundColor: '#fff3e0' }}>
              <Typography variant="h6" gutterBottom color="warning.dark">
                ⚠️ Recommandations
              </Typography>
              <Typography variant="body2" paragraph>
                • Agissez rapidement pour préserver vos droits
              </Typography>
              <Typography variant="body2" paragraph>
                • Conservez toutes les preuves (contrats, factures, emails)
              </Typography>
              <Typography variant="body2" paragraph>
                • Le recouvrement amiable est toujours recommandé en première intention
              </Typography>
              <Typography variant="body2">
                • Nos experts vous conseilleront sur la meilleure stratégie
              </Typography>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RecoveryForm;